import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Lock, ShieldAlert } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { ForgotPasswordModal } from '../../components/auth/ForgotPasswordModal';
import { NAV_ROUTES } from '../../utils/constants';
import {
  confirmarCorreo,
  correoDelCodigo,
  definirContrasena,
  fijarSilo,
  leerAccionDeLaURL,
  mensajeDeErrorDeAccion,
  revertirCambioDeCorreo,
  type AccionDeCorreo,
} from '../../lib/auth/actionCode';

type Fase = 'cargando' | 'pedir-contrasena' | 'listo' | 'error';

/**
 * Manejador de acciones de correo de Identity Platform.
 *
 * Es NUESTRA pantalla, no la que Firebase hospeda en `__/auth/action`, por dos
 * razones: la persona no sale de la app, y aquí podemos fijar el `tenantId`
 * que trae el enlace — sin él, el código de un usuario de tenant no se puede
 * aplicar (los usuarios viven dentro del silo, no en el proyecto raíz).
 *
 * 🚨 Esta ruta NO redirige nunca, ni con sesión iniciada. Alguien con su
 * sesión abierta puede estar abriendo la invitación de otra persona, y
 * mandarlo a /dashboard gastaría el código sin usarlo.
 */
export default function AuthActionPage() {
  const navigate = useNavigate();
  const [accion, setAccion] = useState<AccionDeCorreo | null>(null);
  const [fase, setFase] = useState<Fase>('cargando');
  const [correo, setCorreo] = useState('');
  const [nueva, setNueva] = useState('');
  const [confirmacion, setConfirmacion] = useState('');
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [pedirOtro, setPedirOtro] = useState(false);

  useEffect(() => {
    const leida = leerAccionDeLaURL(window.location.href);
    if (!leida) {
      setFase('error');
      setError('Este enlace no trae ninguna acción. Ábrelo tal cual llegó al correo, sin recortarlo.');
      return;
    }
    // Antes que nada: el silo. Cualquier llamada previa fallaría con un
    // "código inválido" que parecería un enlace caducado.
    fijarSilo(leida.tenantId);
    setAccion(leida);

    void (async () => {
      try {
        switch (leida.modo) {
          case 'resetPassword':
            setCorreo(await correoDelCodigo(leida.codigo));
            setFase('pedir-contrasena');
            return;
          case 'verifyEmail':
            await confirmarCorreo(leida.codigo);
            setMensaje('Tu correo quedó confirmado.');
            setFase('listo');
            return;
          case 'recoverEmail': {
            const anterior = await revertirCambioDeCorreo(leida.codigo);
            setMensaje(
              anterior
                ? `Tu correo de acceso volvió a ser ${anterior}. Cambia tu contraseña cuanto antes.`
                : 'Tu correo de acceso quedó revertido. Cambia tu contraseña cuanto antes.',
            );
            setFase('listo');
            return;
          }
          default:
            setFase('error');
            setError('Este tipo de enlace no está soportado por la aplicación.');
        }
      } catch (err) {
        setFase('error');
        setError(mensajeDeErrorDeAccion(err));
      }
    })();
  }, []);

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (nueva !== confirmacion) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    if (!accion) return;

    setGuardando(true);
    try {
      await definirContrasena(accion.codigo, nueva);
      setMensaje('Tu contraseña quedó lista. Ya puedes iniciar sesión.');
      setFase('listo');
    } catch (err) {
      // La política de contraseñas la impone el SERVIDOR: un rechazo aquí es
      // el único sitio donde la persona se entera de los requisitos reales.
      setError(mensajeDeErrorDeAccion(err));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-page__bg">
        <div className="login-page__bg-orb login-page__bg-orb--1" />
        <div className="login-page__bg-orb login-page__bg-orb--2" />
        <div className="login-page__bg-grid" />
      </div>

      <div className="login-page__card">
        <div className="login-page__logo">
          <h1 className="login-page__logo-name">Paitron Radar</h1>
        </div>

        {fase === 'cargando' && <p className="login-page__subtitle">Verificando el enlace…</p>}

        {fase === 'pedir-contrasena' && (
          <>
            <h2 className="login-page__title">Define tu contraseña</h2>
            {/* El correo se muestra, no se pide: viene validado del código y
                dejarlo editable invitaría a pensar que se puede cambiar. */}
            <p className="login-page__subtitle">
              Para la cuenta <strong>{correo}</strong>
            </p>
            <form onSubmit={guardar} className="login-page__form" id="auth-action-form">
              <Input
                id="auth-action-password"
                label="Contraseña nueva"
                type="password"
                value={nueva}
                onChange={e => setNueva(e.target.value)}
                leftIcon={<Lock size={16} />}
                required
              />
              <Input
                id="auth-action-password-confirm"
                label="Repite la contraseña"
                type="password"
                value={confirmacion}
                onChange={e => setConfirmacion(e.target.value)}
                leftIcon={<Lock size={16} />}
                required
              />
              {error && <p className="login-page__error">{error}</p>}
              <Button type="submit" variant="primary" isLoading={guardando} style={{ width: '100%' }}>
                Guardar y continuar
              </Button>
            </form>
          </>
        )}

        {fase === 'listo' && (
          <div style={{ textAlign: 'center' }}>
            <CheckCircle2 size={40} style={{ color: '#10B981' }} />
            <h2 className="login-page__title">Listo</h2>
            <p className="login-page__subtitle">{mensaje}</p>
            <Button
              variant="primary"
              onClick={() => navigate(NAV_ROUTES.LOGIN, { replace: true })}
              style={{ width: '100%', marginTop: '1rem' }}
            >
              Ir a iniciar sesión
            </Button>
          </div>
        )}

        {fase === 'error' && (
          <div style={{ textAlign: 'center' }}>
            <ShieldAlert size={40} style={{ color: '#F59E0B' }} />
            <h2 className="login-page__title">No se pudo usar el enlace</h2>
            <p className="login-page__subtitle">{error}</p>
            {/* Un enlace vencido o ya usado es el caso NORMAL, no la
                excepción: la salida tiene que estar aquí mismo. */}
            <Button variant="primary" onClick={() => setPedirOtro(true)} style={{ width: '100%', marginTop: '1rem' }}>
              Pedir un enlace nuevo
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate(NAV_ROUTES.LOGIN, { replace: true })}
              style={{ width: '100%', marginTop: '0.5rem' }}
            >
              Volver a iniciar sesión
            </Button>
          </div>
        )}
      </div>

      <ForgotPasswordModal isOpen={pedirOtro} onClose={() => setPedirOtro(false)} />
    </div>
  );
}
